import { createContext, useContext, useState, useCallback, ReactNode, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

interface FluidNavigationContextType {
  currentIndex: number;
  setCurrentIndex: (index: number) => void;
  navigateToIndex: (index: number) => void;
  pages: string[];
}

const FluidNavigationContext = createContext<FluidNavigationContextType | undefined>(undefined);

const NAVIGATION_PAGES = ['/dashboard', '/tasks', '/calendar', '/persons', '/documents'];

export const FluidNavigationProvider = ({ children }: { children: ReactNode }) => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Get initial index from current location
  const getIndexFromPath = useCallback((path: string) => {
    const index = NAVIGATION_PAGES.findIndex(page => {
      if (page === '/dashboard') return path === '/dashboard';
      if (page === '/tasks') return path.startsWith('/tasks') || path.includes('task-list');
      if (page === '/calendar') return path.startsWith('/calendar') || path.includes('event');
      if (page === '/persons') return path.startsWith('/persons') || path.includes('person') || path.includes('contact');
      if (page === '/documents') return path.startsWith('/documents');
      return path === page;
    });
    return Math.max(0, index);
  }, []);

  const [currentIndex, setCurrentIndex] = useState(() => getIndexFromPath(location.pathname));

  // Sync with URL changes
  useEffect(() => {
    const newIndex = getIndexFromPath(location.pathname);
    if (newIndex !== currentIndex) {
      setCurrentIndex(newIndex);
    }
  }, [location.pathname, currentIndex, getIndexFromPath]);

  const navigateToIndex = useCallback((index: number) => {
    if (index >= 0 && index < NAVIGATION_PAGES.length && index !== currentIndex) {
      setCurrentIndex(index);
      navigate(NAVIGATION_PAGES[index]);
    }
  }, [currentIndex, navigate]);

  return (
    <FluidNavigationContext.Provider value={{
      currentIndex,
      setCurrentIndex,
      navigateToIndex,
      pages: NAVIGATION_PAGES
    }}>
      {children}
    </FluidNavigationContext.Provider>
  );
};

export const useFluidNavigation = () => {
  const context = useContext(FluidNavigationContext);
  if (context === undefined) {
    throw new Error('useFluidNavigation must be used within a FluidNavigationProvider');
  }
  return context;
};